import { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Flex,
  Text,
  Button,
  Input,
  Select,
  Table,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Badge,
  Spinner,
  IconButton,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Tooltip,
  InputGroup,
  InputLeftElement,
  SimpleGrid,
  Divider,
  useToast,
  Icon,
  Stack,
  HStack,
} from "@chakra-ui/react";
import { SearchIcon, ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { FiDownload, FiRefreshCw, FiEye, FiUser, FiShield, FiActivity } from "react-icons/fi";
import { MdOutlineHistory } from "react-icons/md";
import BreadCrumbMaker from "../../components/BreadCrumbMaker";
import {
  fetchAuditLogs,
  fetchAuditStats,
  exportAuditLogs,
} from "../../actions/auditLogs.action";

/* ─── constants ─────────────────────────────────────────────────── */
const ACTION_BADGE = {
  CREATE:         { bg: "#DCFCE7", color: "#15803D" },
  UPDATE:         { bg: "#DBEAFE", color: "#1D4ED8" },
  DELETE:         { bg: "#FEE2E2", color: "#B91C1C" },
  SUSPEND:        { bg: "#FEF3C7", color: "#B45309" },
  UNSUSPEND:      { bg: "#CCFBF1", color: "#0F766E" },
  PASSWORD_RESET: { bg: "#EDE9FE", color: "#6D28D9" },
};

const MODULE_BADGE = {
  ADMIN:  { bg: "#EDE9FE", color: "#6D28D9" },
  ROLES:  { bg: "#DBEAFE", color: "#1D4ED8" },
  ACCESS: { bg: "#FEF3C7", color: "#B45309" },
};

const RESULT_BADGE = {
  SUCCESS: { bg: "#DCFCE7", color: "#15803D" },
  FAILURE: { bg: "#FEE2E2", color: "#B91C1C" },
};

/* ─── column search input ────────────────────────────────────────── */
const SearchTh = ({ value, onChange, placeholder, minW }) => (
  <Th py={2} minW={minW}>
    <Input
      size="xs"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      bg="white"
      border="1px solid #E2E8F0"
      borderRadius="6px"
      _focus={{ borderColor: "#4C7CF7", boxShadow: "none" }}
    />
  </Th>
);

/* ─── stat card ─────────────────────────────────────────────────── */
const StatCard = ({ label, value, sub, iconBg, icon, iconColor }) => (
  <Box
    bg="white"
    p={4}
    borderRadius="3xl"
    border="1px solid #D0D5DD"
    display="flex"
    alignItems="center"
    h="100px"
    gap={3}
  >
    <Box
      bg={iconBg}
      p={3}
      borderRadius="full"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      <Icon as={icon} boxSize={5} color={iconColor} />
    </Box>
    <Flex direction="column" align="flex-start">
      <Text fontSize="13px" color="#718EBF">{label}</Text>
      <Text fontSize="22px" fontWeight="600" lineHeight="1.2">{value}</Text>
      {sub && <Text fontSize="11px" color="#A0AEC0">{sub}</Text>}
    </Flex>
  </Box>
);

/* ─── main component ─────────────────────────────────────────────── */
export default function AuditLogs() {
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [logs, setLogs]             = useState([]);
  const [stats, setStats]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [exporting, setExporting]   = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);

  const [page, setPage]             = useState(1);
  const [pageSize, setPageSize]     = useState(25);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs]   = useState(0);

  const [filters, setFilters] = useState({
    module: "", actionType: "", startDate: "", endDate: "",
  });

  const [colSearch, setColSearch] = useState({
    search: "", resourceName: "", performedBy: "",
  });
  const [debouncedCol, setDebouncedCol] = useState(colSearch);
  const debounceRef = useRef(null);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedCol(colSearch), 350);
    return () => clearTimeout(debounceRef.current);
  }, [colSearch]);

  /* ─── data fetching ─────────────────────────────────── */
  const loadLogs = useCallback(async (opts = {}) => {
    opts.isRefresh ? setRefreshing(true) : setLoading(true);
    try {
      const res = await fetchAuditLogs({
        page,
        limit: pageSize,
        ...filters,
        search:
          debouncedCol.search || debouncedCol.resourceName || debouncedCol.performedBy,
      });
      setLogs(res.data || []);
      setTotalPages(res.pagination?.totalPages || 1);
      setTotalLogs(res.pagination?.total || 0);
    } catch (err) {
      toast({ title: "Error", description: err.message, status: "error", duration: 3000 });
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [page, pageSize, filters, debouncedCol, toast]);

  const loadStats = useCallback(async () => {
    try {
      const data = await fetchAuditStats({
        startDate: filters.startDate,
        endDate: filters.endDate,
      });
      setStats(data);
    } catch { /* silent */ }
  }, [filters.startDate, filters.endDate]);

  useEffect(() => { loadLogs(); }, [loadLogs]);
  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { setPage(1); }, [filters, debouncedCol, pageSize]);

  /* ─── handlers ──────────────────────────────────────── */
  const handleRefresh = () => {
    loadLogs({ isRefresh: true });
    loadStats();
  };

  const handleFilterChange = (field, value) =>
    setFilters((prev) => ({ ...prev, [field]: value }));

  const handleColSearch = (field, value) =>
    setColSearch((prev) => ({ ...prev, [field]: value }));

  const resetFilters = () => {
    setFilters({ module: "", actionType: "", startDate: "", endDate: "" });
    setColSearch({ search: "", resourceName: "", performedBy: "" });
    setPage(1);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      await exportAuditLogs(filters, "csv");
      toast({ title: "Exported successfully", status: "success", duration: 2000 });
    } catch (err) {
      toast({ title: "Export failed", description: err.message, status: "error", duration: 3000 });
    } finally {
      setExporting(false);
    }
  };

  const openDetail = (log) => { setSelectedLog(log); onOpen(); };

  const fmt = (d) => {
    if (!d) return "-";
    return new Date(d).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  };

  /* ─── render ─────────────────────────────────────────── */
  return (
    <Box w="100%" bg="#F5F7FA" p={6}>

      {/* BREADCRUMB */}
      <BreadCrumbMaker
        items={[
          { title: "Access Control", link: "/settings" },
          { title: "Audit Logs", link: "/settings/audit-logs" },
        ]}
      />

      {/* HEADER */}
      <Flex justify="space-between" align="center" mt={2} mb={5}>
        <Text fontSize="2xl" fontWeight="700">Audit Logs</Text>
        <Flex gap={2}>
          <Button
            size="sm"
            h="40px"
            px={4}
            borderRadius="10px"
            border="1px solid #D0D5DD"
            bg="white"
            fontWeight="500"
            color="gray.600"
            leftIcon={<Icon as={FiRefreshCw} />}
            onClick={handleRefresh}
            isLoading={refreshing}
            loadingText="Refreshing"
            _hover={{ bg: "#F5F7FA" }}
          >
            Refresh
          </Button>
          <Button
            size="sm"
            h="40px"
            px={5}
            borderRadius="10px"
            bg="#4C7CF7"
            color="white"
            fontWeight="500"
            leftIcon={<Icon as={FiDownload} />}
            onClick={handleExport}
            isLoading={exporting}
            loadingText="Exporting"
            _hover={{ bg: "#3b6ae0" }}
          >
            Export CSV
          </Button>
        </Flex>
      </Flex>

      {/* STAT CARDS */}
      <SimpleGrid columns={{ base: 1, sm: 2, md: 4 }} gap={4} mb={5}>
        <StatCard
          label="Total Logs"
          value={stats?.totalLogs ?? "—"}
          sub="All time"
          iconBg="#E7EDFF"
          icon={MdOutlineHistory}
          iconColor="#396AFF"
        />
        <StatCard
          label="Employee Actions"
          value={stats?.moduleStats?.ADMIN ?? "—"}
          sub="Creates, updates, deletes"
          iconBg="#FFF5D9"
          icon={FiUser}
          iconColor="#FFBB38"
        />
        <StatCard
          label="Role Actions"
          value={stats?.moduleStats?.ROLES ?? "—"}
          sub="Permission changes"
          iconBg="#E8FFF6"
          icon={FiShield}
          iconColor="#16C79A"
        />
        <StatCard
          label="Creates / Deletes"
          value={`${stats?.actionTypeStats?.CREATE ?? 0} / ${stats?.actionTypeStats?.DELETE ?? 0}`}
          sub="Action breakdown"
          iconBg="#FFE9E9"
          icon={FiActivity}
          iconColor="#FF4747"
        />
      </SimpleGrid>

      {/* GLOBAL FILTER BAR */}
      <Box
        bg="white"
        borderRadius="16px"
        border="1px solid #E5E7EB"
        boxShadow="0 2px 8px rgba(0,0,0,0.04)"
        p={4}
        mb={4}
      >
        <Flex gap={3} wrap="wrap" align="flex-end">
          <Box flex="1" minW="200px">
            <Text fontSize="xs" color="gray.500" mb={1} fontWeight="500">Search</Text>
            <InputGroup size="sm">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder="Name, email, description..."
                value={colSearch.search}
                onChange={(e) => handleColSearch("search", e.target.value)}
                bg="#F9FAFB"
                border="1px solid #E2E8F0"
                borderRadius="8px"
                _focus={{ borderColor: "#4C7CF7", boxShadow: "none", bg: "white" }}
              />
            </InputGroup>
          </Box>

          <Box minW="140px">
            <Text fontSize="xs" color="gray.500" mb={1} fontWeight="500">Module</Text>
            <Select
              size="sm"
              value={filters.module}
              onChange={(e) => handleFilterChange("module", e.target.value)}
              bg="#F9FAFB"
              border="1px solid #E2E8F0"
              borderRadius="8px"
              _focus={{ borderColor: "#4C7CF7", boxShadow: "none" }}
            >
              <option value="">All Modules</option>
              <option value="ADMIN">Employees</option>
              <option value="ROLES">Roles</option>
            </Select>
          </Box>

          <Box minW="150px">
            <Text fontSize="xs" color="gray.500" mb={1} fontWeight="500">Action</Text>
            <Select
              size="sm"
              value={filters.actionType}
              onChange={(e) => handleFilterChange("actionType", e.target.value)}
              bg="#F9FAFB"
              border="1px solid #E2E8F0"
              borderRadius="8px"
              _focus={{ borderColor: "#4C7CF7", boxShadow: "none" }}
            >
              <option value="">All Actions</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="DELETE">Delete</option>
              <option value="SUSPEND">Suspend</option>
              <option value="UNSUSPEND">Unsuspend</option>
              <option value="PASSWORD_RESET">Password Reset</option>
            </Select>
          </Box>

          <Box minW="135px">
            <Text fontSize="xs" color="gray.500" mb={1} fontWeight="500">From Date</Text>
            <Input
              size="sm"
              type="date"
              value={filters.startDate}
              onChange={(e) => handleFilterChange("startDate", e.target.value)}
              bg="#F9FAFB"
              border="1px solid #E2E8F0"
              borderRadius="8px"
              _focus={{ borderColor: "#4C7CF7", boxShadow: "none" }}
            />
          </Box>

          <Box minW="135px">
            <Text fontSize="xs" color="gray.500" mb={1} fontWeight="500">To Date</Text>
            <Input
              size="sm"
              type="date"
              value={filters.endDate}
              onChange={(e) => handleFilterChange("endDate", e.target.value)}
              bg="#F9FAFB"
              border="1px solid #E2E8F0"
              borderRadius="8px"
              _focus={{ borderColor: "#4C7CF7", boxShadow: "none" }}
            />
          </Box>

          <Button
            size="sm"
            h="32px"
            variant="ghost"
            color="gray.500"
            onClick={resetFilters}
            _hover={{ color: "gray.800" }}
          >
            Clear
          </Button>
        </Flex>
      </Box>

      {/* TABLE CARD */}
      <Box
        bg="white"
        borderRadius="16px"
        border="1px solid #E5E7EB"
        boxShadow="0 2px 8px rgba(0,0,0,0.04)"
        overflow="hidden"
        position="relative"
      >
        {/* Loading overlay */}
        {(loading || refreshing) && (
          <Box
            position="absolute"
            inset="0"
            bg="rgba(255,255,255,0.75)"
            display="flex"
            alignItems="center"
            justifyContent="center"
            zIndex={10}
          >
            <Spinner size="lg" color="#4C7CF7" />
          </Box>
        )}

        <Box overflowX="auto">
          <Table size="sm">
            <Thead>
              {/* Column headers */}
              <Tr bg="#F9FAFB">
                <Th w="50px">Sr.</Th>
                <Th>Timestamp</Th>
                <Th>Action</Th>
                <Th>Module</Th>
                <Th>Resource</Th>
                <Th>Performed By</Th>
                <Th>Description</Th>
                <Th w="90px" textAlign="center">Result</Th>
                <Th w="60px" />
              </Tr>

              {/* Filter row */}
              <Tr bg="#E0EAFF">
                <Th />
                <Th />
                <Th />
                <Th />
                {/* <SearchTh
                  placeholder="Resource name"
                  value={colSearch.resourceName}
                  onChange={(e) => handleColSearch("resourceName", e.target.value)}
                  minW="130px"
                />
                <SearchTh
                  placeholder="Performed by"
                  value={colSearch.performedBy}
                  onChange={(e) => handleColSearch("performedBy", e.target.value)}
                  minW="140px"
                /> */}
                <Th />
                <Th />
                <Th />
              </Tr>
            </Thead>

            <Tbody>
              {!loading && logs.length === 0 ? (
                <Tr>
                  <Td colSpan={9} textAlign="center" py={10} color="gray.400">
                    No audit logs found
                  </Td>
                </Tr>
              ) : (
                logs.map((log, idx) => {
                  const actionStyle = ACTION_BADGE[log.actionType] || { bg: "#F3F4F6", color: "#374151" };
                  const moduleStyle = MODULE_BADGE[log.module]     || { bg: "#F3F4F6", color: "#374151" };
                  const resultStyle = RESULT_BADGE[log.result]     || { bg: "#F3F4F6", color: "#374151" };

                  return (
                    <Tr
                      key={log._id}
                      _hover={{ bg: "#F5F8FF", cursor: "pointer" }}
                      transition="0.15s"
                      borderBottom="1px solid #F0F2F5"
                      onClick={() => openDetail(log)}
                    >
                      <Td color="gray.500">
                        {String((page - 1) * pageSize + idx + 1).padStart(2, "0")}.
                      </Td>
                      <Td whiteSpace="nowrap" fontSize="xs" color="gray.600">
                        {fmt(log.createdAt)}
                      </Td>
                      <Td>
                        <Badge
                          px={2} py={0.5}
                          borderRadius="full"
                          bg={actionStyle.bg}
                          color={actionStyle.color}
                          fontSize="11px"
                          fontWeight="600"
                        >
                          {log.actionType}
                        </Badge>
                      </Td>
                      <Td>
                        <Badge
                          px={2} py={0.5}
                          borderRadius="full"
                          bg={moduleStyle.bg}
                          color={moduleStyle.color}
                          fontSize="11px"
                        >
                          {log.module === "ADMIN" ? "EMPLOYEE" : log.module}
                        </Badge>
                      </Td>
                      <Td>
                        <Text fontWeight="500" fontSize="sm">{log.resourceName || "-"}</Text>
                        <Text fontSize="11px" color="#A0AEC0" textTransform="capitalize">
                          {log.resourceType}
                        </Text>
                      </Td>
                      <Td>
                        <Text fontWeight="500" fontSize="sm">
                          {log.performedByInfo?.name || "-"}
                        </Text>
                        <Text fontSize="11px" color="#A0AEC0">
                          {log.performedByInfo?.email || ""}
                        </Text>
                      </Td>
                      <Td maxW="260px">
                        <Tooltip label={log.changesDescription} placement="top" hasArrow>
                          <Text fontSize="sm" isTruncated color="gray.600">
                            {log.changesDescription || "-"}
                          </Text>
                        </Tooltip>
                      </Td>
                      <Td textAlign="center">
                        <Badge
                          px={2} py={0.5}
                          borderRadius="full"
                          bg={resultStyle.bg}
                          color={resultStyle.color}
                          fontSize="11px"
                        >
                          {log.result}
                        </Badge>
                      </Td>
                      <Td onClick={(e) => e.stopPropagation()}>
                        <Tooltip label="View Details">
                          <IconButton
                            icon={<FiEye />}
                            size="sm"
                            variant="ghost"
                            color="#4C7CF7"
                            onClick={() => openDetail(log)}
                            aria-label="View details"
                            _hover={{ bg: "#E7EDFF" }}
                          />
                        </Tooltip>
                      </Td>
                    </Tr>
                  );
                })
              )}
            </Tbody>
          </Table>
        </Box>

        {/* PAGINATION */}
        <Flex
          justify="space-between"
          align="center"
          px={4}
          py={3}
          borderTop="1px solid #E5E7EB"
        >
          <Text fontSize="sm" color="gray.600">
            Page <b>{page}</b> of <b>{totalPages}</b>
            <Text as="span" color="gray.400" ml={2}>({totalLogs} total)</Text>
          </Text>

          <Flex align="center" gap={2}>
            <Text fontSize="sm" color="gray.600">Rows:</Text>
            <Select
              size="sm"
              width="75px"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
              border="1px solid #D0D5DD"
            >
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </Select>
          </Flex>

          <Flex gap={2}>
            <IconButton
              size="sm"
              variant="outline"
              icon={<ChevronLeftIcon />}
              isDisabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
              aria-label="Prev page"
            />
            <IconButton
              size="sm"
              variant="outline"
              icon={<ChevronRightIcon />}
              isDisabled={page >= totalPages}
              onClick={() => setPage((p) => p + 1)}
              aria-label="Next page"
            />
          </Flex>
        </Flex>
      </Box>

      {/* ─── DETAIL MODAL ─────────────────────────────────────── */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent borderRadius="16px">
          <ModalHeader borderBottom="1px solid #E5E7EB" pb={3}>
            <Flex align="center" gap={3}>
              <Text fontSize="md" fontWeight="600">Log Details</Text>
              {selectedLog && (
                <>
                  <Badge
                    px={2} py={0.5} borderRadius="full" fontSize="11px" fontWeight="600"
                    bg={ACTION_BADGE[selectedLog.actionType]?.bg || "#F3F4F6"}
                    color={ACTION_BADGE[selectedLog.actionType]?.color || "#374151"}
                  >
                    {selectedLog.actionType}
                  </Badge>
                  <Badge
                    px={2} py={0.5} borderRadius="full" fontSize="11px"
                    bg={MODULE_BADGE[selectedLog.module]?.bg || "#F3F4F6"}
                    color={MODULE_BADGE[selectedLog.module]?.color || "#374151"}
                  >
                    {selectedLog.module}
                  </Badge>
                </>
              )}
            </Flex>
          </ModalHeader>
          <ModalCloseButton />

          <ModalBody py={5}>
            {selectedLog && (
              <Stack spacing={4}>
                <SimpleGrid columns={2} gap={4}>
                  <Box>
                    <Text fontSize="11px" color="#718EBF" mb={0.5} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Timestamp</Text>
                    <Text fontSize="sm" fontWeight="500">{fmt(selectedLog.createdAt)}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="11px" color="#718EBF" mb={0.5} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Result</Text>
                    <Badge
                      px={2} py={0.5} borderRadius="full" fontSize="11px"
                      bg={RESULT_BADGE[selectedLog.result]?.bg || "#F3F4F6"}
                      color={RESULT_BADGE[selectedLog.result]?.color || "#374151"}
                    >
                      {selectedLog.result}
                    </Badge>
                  </Box>
                  <Box>
                    <Text fontSize="11px" color="#718EBF" mb={0.5} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Resource</Text>
                    <Text fontSize="sm" fontWeight="500">{selectedLog.resourceName || String(selectedLog.resourceId)}</Text>
                    <Text fontSize="11px" color="#A0AEC0" textTransform="capitalize">{selectedLog.resourceType}</Text>
                  </Box>
                  <Box>
                    <Text fontSize="11px" color="#718EBF" mb={0.5} fontWeight="600" textTransform="uppercase" letterSpacing="wide">IP Address</Text>
                    <Text fontSize="sm">{selectedLog.ipAddress || "-"}</Text>
                  </Box>
                </SimpleGrid>

                <Divider borderColor="#E5E7EB" />

                <Box>
                  <Text fontSize="11px" color="#718EBF" mb={1} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Performed By</Text>
                  <Flex align="center" gap={3} bg="#F9FAFB" p={3} borderRadius="10px" border="1px solid #E5E7EB">
                    <Box bg="#E7EDFF" p={2} borderRadius="full">
                      <Icon as={FiUser} boxSize={4} color="#396AFF" />
                    </Box>
                    <Box>
                      <Text fontWeight="600" fontSize="sm">{selectedLog.performedByInfo?.name || "-"}</Text>
                      <Text fontSize="xs" color="gray.500">{selectedLog.performedByInfo?.email}</Text>
                    </Box>
                    <Badge ml="auto" px={2} py={0.5} borderRadius="full" bg="#E9EEFF" color="#4C7CF7" fontSize="11px">
                      {selectedLog.performedByInfo?.role || "-"}
                    </Badge>
                  </Flex>
                </Box>

                <Box>
                  <Text fontSize="11px" color="#718EBF" mb={1} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Description</Text>
                  <Text fontSize="sm" color="gray.700" bg="#F9FAFB" p={3} borderRadius="10px" border="1px solid #E5E7EB">
                    {selectedLog.changesDescription || "-"}
                  </Text>
                </Box>

                {selectedLog.changedFields?.length > 0 && (
                  <Box>
                    <Text fontSize="11px" color="#718EBF" mb={2} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Changed Fields</Text>
                    <HStack flexWrap="wrap" gap={2}>
                      {selectedLog.changedFields.map((field) => (
                        <Badge key={field} px={2} py={0.5} borderRadius="full" bg="#E0EAFF" color="#1D4ED8" fontSize="11px">
                          {field}
                        </Badge>
                      ))}
                    </HStack>
                  </Box>
                )}

                {(selectedLog.changes?.before || selectedLog.changes?.after) && (
                  <>
                    <Divider borderColor="#E5E7EB" />
                    <SimpleGrid columns={{ base: 1, md: 2 }} gap={4}>
                      {selectedLog.changes?.before && (
                        <Box>
                          <Text fontSize="11px" color="#718EBF" mb={1} fontWeight="600" textTransform="uppercase" letterSpacing="wide">Before</Text>
                          <Box
                            bg="#FFF5F5"
                            border="1px solid #FED7D7"
                            p={3}
                            borderRadius="10px"
                            fontSize="11px"
                            maxH="220px"
                            overflowY="auto"
                            fontFamily="mono"
                            color="#742A2A"
                          >
                            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                              {JSON.stringify(selectedLog.changes.before, null, 2)}
                            </pre>
                          </Box>
                        </Box>
                      )}
                      {selectedLog.changes?.after && (
                        <Box>
                          <Text fontSize="11px" color="#718EBF" mb={1} fontWeight="600" textTransform="uppercase" letterSpacing="wide">After</Text>
                          <Box
                            bg="#F0FFF4"
                            border="1px solid #C6F6D5"
                            p={3}
                            borderRadius="10px"
                            fontSize="11px"
                            maxH="220px"
                            overflowY="auto"
                            fontFamily="mono"
                            color="#22543D"
                          >
                            <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                              {JSON.stringify(selectedLog.changes.after, null, 2)}
                            </pre>
                          </Box>
                        </Box>
                      )}
                    </SimpleGrid>
                  </>
                )}
              </Stack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
}
